import React, { useState } from 'react';

const SalaryStructureSimulator = ({ salaryComponents, salaryStructures }) => {
  const [simulationData, setSimulationData] = useState({
    salaryType: 'ctc', // 'ctc' or 'gross'
    amount: '',
    branch: '',
    selectedStructure: '',
    statutoryApplicabilities: {
      pf: true,
      esi: true,
      pt: true,
      gratuity: true
    },
    results: null
  });

  const handleInputChange = (field, value) => {
    setSimulationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStatutoryChange = (statutory, value) => {
    setSimulationData(prev => ({
      ...prev,
      statutoryApplicabilities: {
        ...prev.statutoryApplicabilities,
        [statutory]: value
      }
    }));
  };

  const calculateSalary = () => {
    const amount = parseFloat(simulationData.amount);
    if (!amount || amount <= 0) {
      alert('Please enter a valid salary amount');
      return;
    }

    // Mock calculation - in real implementation, this would use actual formulas
    const calculations = {
      basic: amount * 0.4, // 40% of CTC/Gross
      hra: amount * 0.2,   // 20% of CTC/Gross
      allowances: amount * 0.3, // 30% of CTC/Gross
      pfEmployee: simulationData.statutoryApplicabilities.pf ? Math.min(amount * 0.12, 15000 * 0.12) : 0,
      pfEmployer: simulationData.statutoryApplicabilities.pf ? Math.min(amount * 0.12, 15000 * 0.12) : 0,
      esiEmployee: simulationData.statutoryApplicabilities.esi ? Math.min(amount * 0.0075, 21000 * 0.0075) : 0,
      esiEmployer: simulationData.statutoryApplicabilities.esi ? Math.min(amount * 0.0325, 21000 * 0.0325) : 0,
      pt: simulationData.statutoryApplicabilities.pt ? 200 : 0,
      gratuity: simulationData.statutoryApplicabilities.gratuity ? amount * 0.0041 : 0 // Approximate
    };

    const grossSalary = calculations.basic + calculations.hra + calculations.allowances;
    const totalDeductions = calculations.pfEmployee + calculations.esiEmployee + calculations.pt;
    const netSalary = grossSalary - totalDeductions;
    const totalCost = amount + calculations.pfEmployer + calculations.esiEmployer + calculations.gratuity;

    setSimulationData(prev => ({
      ...prev,
      results: {
        ...calculations,
        grossSalary,
        totalDeductions,
        netSalary,
        totalCost
      }
    }));
  };

  const resetSimulation = () => {
    setSimulationData({
      salaryType: 'ctc',
      amount: '',
      branch: '',
      selectedStructure: '',
      statutoryApplicabilities: {
        pf: true,
        esi: true,
        pt: true,
        gratuity: true
      },
      results: null
    });
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          Salary Structure Simulator
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Simulation Inputs</h3>
            
            {/* Salary Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salary Type *
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="salaryType"
                    value="ctc"
                    checked={simulationData.salaryType === 'ctc'}
                    onChange={(e) => handleInputChange('salaryType', e.target.value)}
                    className="mr-2"
                  />
                  CTC (Cost to Company)
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="salaryType"
                    value="gross"
                    checked={simulationData.salaryType === 'gross'}
                    onChange={(e) => handleInputChange('salaryType', e.target.value)}
                    className="mr-2"
                  />
                  Gross Salary
                </label>
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {simulationData.salaryType === 'ctc' ? 'CTC Amount' : 'Gross Salary Amount'} (₹) *
              </label>
              <input
                type="number"
                value={simulationData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Branch */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch *
              </label>
              <select
                value={simulationData.branch}
                onChange={(e) => handleInputChange('branch', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select Branch</option>
                <option value="bangalore">Bangalore</option>
                <option value="mumbai">Mumbai</option>
                <option value="delhi">Delhi</option>
                <option value="chennai">Chennai</option>
                <option value="hyderabad">Hyderabad</option>
                <option value="pune">Pune</option>
              </select>
            </div>

            {/* Salary Structure */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salary Structure
              </label>
              <select
                value={simulationData.selectedStructure}
                onChange={(e) => handleInputChange('selectedStructure', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select Structure (Optional)</option>
                {salaryStructures.map((structure) => (
                  <option key={structure.id} value={structure.id}>
                    {structure.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Statutory Applicabilities */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statutory Applicabilities
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={simulationData.statutoryApplicabilities.pf}
                    onChange={(e) => handleStatutoryChange('pf', e.target.checked)}
                    className="mr-2"
                  />
                  Provident Fund (PF)
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={simulationData.statutoryApplicabilities.esi}
                    onChange={(e) => handleStatutoryChange('esi', e.target.checked)}
                    className="mr-2"
                  />
                  Employee State Insurance (ESI)
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={simulationData.statutoryApplicabilities.pt}
                    onChange={(e) => handleStatutoryChange('pt', e.target.checked)}
                    className="mr-2"
                  />
                  Professional Tax (PT)
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={simulationData.statutoryApplicabilities.gratuity}
                    onChange={(e) => handleStatutoryChange('gratuity', e.target.checked)}
                    className="mr-2"
                  />
                  Gratuity
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={calculateSalary}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Calculate Salary
              </button>
              <button
                onClick={resetSimulation}
                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Calculation Results</h3>
            
            {simulationData.results ? (
              <div className="space-y-4">
                {/* Salary Breakdown */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Salary Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Basic Salary:</span>
                      <span>₹{simulationData.results.basic.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>HRA:</span>
                      <span>₹{simulationData.results.hra.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Allowances:</span>
                      <span>₹{simulationData.results.allowances.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-medium border-t pt-2">
                      <span>Gross Salary:</span>
                      <span>₹{simulationData.results.grossSalary.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Deductions</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>PF (Employee):</span>
                      <span>₹{simulationData.results.pfEmployee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>ESI (Employee):</span>
                      <span>₹{simulationData.results.esiEmployee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Professional Tax:</span>
                      <span>₹{simulationData.results.pt.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-medium border-t pt-2 text-red-600">
                      <span>Total Deductions:</span>
                      <span>₹{simulationData.results.totalDeductions.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Employer Contributions */}
                <div className="bg-redhalo-mint/40 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Employer Contributions</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>PF (Employer):</span>
                      <span>₹{simulationData.results.pfEmployer.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>ESI (Employer):</span>
                      <span>₹{simulationData.results.esiEmployer.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Gratuity:</span>
                      <span>₹{simulationData.results.gratuity.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Net Salary (Take Home):</span>
                      <span className="font-medium text-green-600">₹{simulationData.results.netSalary.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Cost to Company:</span>
                      <span className="font-medium text-redhalo-navy">₹{simulationData.results.totalCost.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 p-8 rounded-lg text-center">
                <p className="text-gray-500">Enter salary details and click "Calculate Salary" to see results</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryStructureSimulator;
